import type { FC } from 'react';
import dp7 from './../../../resources/content/dp7.png';
import { KadaneAlgorithmWrapper } from './KadaneAlgorithm.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface KadaneAlgorithmProps {}

const KadaneAlgorithm: FC<KadaneAlgorithmProps> = () => (
 <KadaneAlgorithmWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Kadane's Algorithm</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-center">Kadane's Algorithm is designed to find the maximum sum of any contiguous subarray within a given array of numbers. What makes this algorithm special is its simplicity and efficiency—it solves the problem in just one pass through the data.</p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="400" src={dp7} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <ul className="text-align-justify">
        <li className="linear"><strong>Maximum Sum So Far</strong></li>
        <ul className="text-align-justify">
          <li>This tracks the highest sum we've encountered throughout our journey through the array.</li>
        </ul>
        <li className="linear"><strong>Current Sum</strong></li>
        <ul className="text-align-justify">
          <li>This accumulates the sum of our current contiguous subarray.</li>
        </ul>
        <li className="linear"><strong>Resetting Current Sum</strong></li>
        <ul className="text-align-justify">
          <li>When our current sum becomes negative, we reset it to zero and start fresh.</li>
        </ul>
      </ul>
      <br/>
      <h4 className="text-align-left">Logic behind Kadane's Algorithm</h4>
      <p className="text-align-justify">As we move through each element in the array, we ask ourselves a simple question: 'Should I extend my current subarray by including this element, or should I start a new subarray from this element?' We extend the current subarray if adding the new element increases our sum. But here's the key insight: if our current sum ever becomes negative, we immediately reset it to zero. Why? Because a negative sum can never help us maximize our total ~ we're better off starting fresh from the next element. At each step, we also update our 'Maximum Sum So Far' if our current sum exceeds it. This ensures we always remember the best result we've found.</p>
      <br/>
      <h4 className="text-align-left">Maximum Subarray</h4>
      <ul className="text-align-justify">
        <li>The maximum sum of a continuous subarray. Important thing to note is:</li>
        <ul className="text-align-justify">
          <li>It must include nums[i]</li>
          <li>You’re not free to skip i</li>
        </ul>
        <li>At each position, we need to decide: should we extend the previous subarray by including the current element, or should we start a new subarray from the current element?</li>
        <li>Our goal is to find which contiguous subarray gives us the maximum sum.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [-2, 1, -3, 4, -1, 2, 1, -5, 4]\n'+
            '\n'+
            'subarray with maxSum:[4, -1, 2, 1]\n'+
            '👉 Answer is 6'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Maximum Circular Subarray Sum</h4>
      <ul className="text-align-justify">
        <li>When the array wraps around in a circle. Wrapping means: Take everything EXCEPT some middle bad part. Middle bad part means the minimum subarray.</li>
        <li>The goal is to find the maximum sum of any non-empty contiguous subarray.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [5, -3, 5]\n'+
            '\n'+
            'Total sum: 5 + (-3) + 5 = 7\n'+
            'Minimum subarray: [-3] → sum = -3\n'+
            'circular_sum = 7 - (-3) = 10\n'+
            'max(7, 10) = 10\n'+
            '\n'+
            '👉 Answer is 10'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Maximum Product Subarray</h4>
      <ul className="text-align-justify">
        <li>Finding the contiguous subarray with the highest product. But, What makes this tricky? The negative numbers. A minimum (negative) value can become maximum after multiplying with a negative number.</li>
        <li>We need to track both maximum AND minimum products as we go, because today's minimum might become tomorrow's maximum.</li>
        <li>The goal is to find the contiguous subarray that has the largest product.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'nums = [2, 3, -2, 4]\n'+
            '\n'+
            'Possible products of subarray: \n'+
            '2, 3, -2, 4, 6, -6, -8, -12, -24, -48\n'+
            '\n'+
            'Subarray with maxProduct: \n'+
            '[2, 3]\n'+
            '\n'+
            '👉 Answer is 6 '
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Best Time to Buy and Sell Stock</h4>
      <ul className="text-align-justify">
        <li>You can buy once and sell once. Here's our stratergy:</li>
        <ul className="text-align-justify">
          <li>Find the lowest price to buy at (up to the current day)</li>
          <li>Calculate potential profit if we sell on the current day</li>
          <li>Track the maximum profit we've seen so far</li>
        </ul>
        <li>The goal is to find the maximum profit by buying on one day and selling on a different future day.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'prices = [7, 1, 5, 3, 6, 4]\n'+
            '\n'+
            'Buy at minimum: price = 1\n'+
            'Sell at maximum after purchase: price = 6\n'+
            'Profit = SellingPrice - BuyingPrice → 6-1 = 5\n'+
            '\n'+
            '👉 Answer is 5'
          }
        </SyntaxHighlighter>
      </ul>

   </div>
 </KadaneAlgorithmWrapper>
);

export default KadaneAlgorithm;
